import { defineComponent, h, markRaw } from 'vue';
import utils from '../../Shared/utils.jsx';
import BaseComponent from './BaseComponentMixin.jsx';
import EventBus from '../event-bus.js';
import api from '@/Services/api';
import careHelpfulFunctions from '../careHelpfulFunctions.jsx';
import careService from '@/Services/careService.jsx';
import { isScopeScriptEmpty } from '@/Shared/scopeLibraryCore.js';
import { nextTick } from 'vue';

export default defineComponent({
    name: 'dynamic-user-control',
    mixins: [BaseComponent],
    props: {
        controlURL: String,
        controlName: String,
        payload: null,
        controlEvents: null,
    },
    data() {
        return {
            key: null,
            control: null,
            headers: {},
            newscopeitems: {},
            uselocalscope: false,
            showMenu: false,
            x: 0,
            y: 0,
            dynamicData: false,

            // Non-reactive members:
            controlurlexpr: null,
            controldataexpr: null,
            controldatavalue: null,
        };
    },
    computed: {
        controlURLvalue() {
            if (this.controlurlexpr)
                return utils.evaluate(this.controlurlexpr, this);
            else
                return '';
        },
        controlDataValue() {
            return this.controldatavalue;
        },
        sizeStyle() {
            return { display: 'flex', flexDirection: 'column', flexGrow: '1', overflow: 'auto', position: 'relative', alignSelf: 'normal' };
        },
        Params() {
            return this.uselocalscope ? this.controldatavalue : this.controlscope;
        },
        Control() {
            return this.uselocalscope ? this.newscopeitems : this.scopeitems;
        },
    },
    created() {
        this.key = utils.generateUUID();

        if (this.controlData) {
            this.controldataexpr = markRaw( this.utils.compileDynamicObject(this, this.controlData) );

            // Intentionally not putting this in the data property - keeps it from getting modified by Vue (which it would do to make it reactive)
            this.controldatavalue = markRaw( this.utils.evaluateDynamicObject(this.controldataexpr, this) );
        }

        if (this.controlURL)
            this.controlurlexpr = markRaw( this.utils.compile(this, this.controlURL) );

        this.watcher$ = this.$watch(
            () => {
                if (this.controlURL)
                    return this.controlURLvalue;
                else
                    return null;
            },
            (val, oldval) => {
                this.Refresh();
            }
        );

        this.$on('Action-EmitControlEvent', this.performEmitControlEvent);
    },
    unmounted() {
        this.watcher$();
        this.$off('Action-EmitControlEvent', this.performEmitControlEvent);

        if (this.scopeitems && this.controlName && (this.controlName in this.scopeitems))
            delete this.scopeitems[this.controlName];
    },
    async mounted() {
        console.log(`UserControl ${this.controlName} mounted, refreshing...`);
        await this.Refresh();
    },
    methods: {
        async refreshchildren(e) {
            utils.api.clearCache();
            await this.Refresh();
            this.key = utils.generateUUID();
        },
        async Refresh() {
            if (this.controlURL) {
                console.log(`UserControl ${this.controlName} refreshing URL: ${this.controlURL}...`);

                try {
                    let apiRequest = {
                        method: 'GET',
                        url: `Document/UserControl/${this.controlURLvalue}`,
                        doNotUseWebsocket: true,
                        flatten: false,
                        cache: this.cacheControl || false
                    };

                    let c = await api.apiRequest(apiRequest);
                    let responseControl = c.data?.Result || c.data;
                    if (!responseControl) {
                        apiRequest.url = `Document/UserControl/public/${this.controlURLvalue}`;
                        c = await api.apiRequest(apiRequest);
                        responseControl = c.data?.Result || c.data;
                    }

                    if (responseControl.$$ScopeItems) {
                        await this.compileScopeItems(responseControl.$$ScopeItems);
                    } else {
                        if (this.scopeitems && this.controlName)
                            this.scopeitems[this.controlName] = {};
                    }

                    this.control = responseControl;
                    this.headers = c.headers;
                } catch (e) {
                    utils.warn(`UserControl ${this.controlURL} failed to load`, e);
                    this.control = {};
                }
            } else {
                this.control = {};
            }

            if (this.controlEvents && this.controlEvents.OnControlLoaded)
                await utils.executeAndCompileAllActions(this.controlEvents.OnControlLoaded, null, this);

            const onCreated = this.newscopeitems?.onCreated;
            if (typeof onCreated === 'function') {
                try {
                    await onCreated();
                } catch (e) {
                    utils.warn(`UserControl ${this.controlName || this.controlURLvalue || ''} onCreated failed`, e);
                }
            }
        },
        async compileScopeItems(items) {
            const public_vars = {};

            utils.detachScopeLibraries(this.newscopeitems);

            let flatHandles = {};
            let rootHandles = {};
            try {
                const resolved = await utils.scopeLibrary.resolveForUserControl(items);
                flatHandles = resolved.flatHandles || {};
                rootHandles = resolved.rootHandles || {};
                if (Object.keys(flatHandles).length)
                    utils.attachScopeLibraries(this.newscopeitems, flatHandles);
            } catch (e) {
                utils.warn('UserControl failed to resolve scope includes', e);
            }

            if (!isScopeScriptEmpty(items.ScopeScript)) {
                this.uselocalscope = true;
                try {
                    utils.compileUserControlScopeScript(items.ScopeScript, {
                        Control: this.newscopeitems,
                        Params: this.controldatavalue,
                        api: utils.apiWrapper,
                        care: careService,
                        util: careHelpfulFunctions,
                        controlModel: this,
                        libraries: rootHandles,
                    });

                    if (items.ScopePublic) {
                        for (let i = 0; i < items.ScopePublic.length; i++) {
                            const name = items.ScopePublic[i];
                            if (name in this.newscopeitems)
                                public_vars[name] = this.newscopeitems[name];
                        }
                    }
                } catch (e) {
                    utils.warn('UserControl failed to compile ScopeScript', e);
                }
                this.uselocalscope = false;
            }
            else {
                this.compileScopeItemsLegacy(items, public_vars);
            }

            if (items.Commands) {
                for (let i = 0; i < items.Commands.length; i++) {
                    const cmd = items.Commands[i];

                    // Include the command as publicly accessible (!cmd.Visibility for backwards compatibility)
                    if (!cmd.Visibility || cmd.Visibility == 'ExternalOnly' || cmd.Visibility == 'InternalAndExternal')
                        public_vars[cmd.Name] = async input => {
                            await utils.executeAndCompileAllActions(cmd.Actions, { Data: input }, this, this.controldatavalue, this.newscopeitems);
                        };
                        //  { Actions: cmd.Actions, ControlScope: this.controldatavalue, ScopeItems: this.newscopeitems };

                    // Include the command as an internal function
                    // Control.<cmdname>(<inputdata>)
                    if (cmd.Visibility == 'InternalOnly' || cmd.Visibility == 'InternalAndExternal') {
                        this.newscopeitems[cmd.Name] = async input => {
                            await utils.executeAndCompileAllActions(cmd.Actions, { Data: input }, this, this.controldatavalue, this.newscopeitems);
                        };
                    }
                }
            }

            this.commands = items.Commands; // I don't think this is used or even good to set here, but not changing it (2026-07-08 DP)

            // Attach all actions defined in the ActionSet array as functions on the Control bag:
            // Control.<actionname>(<inputdata>)
            if (items.ActionSet && Array.isArray(items.ActionSet))
                for (let i = 0; i < items.ActionSet.length; i++) {
                    const act = items.ActionSet[i];
                    this.newscopeitems[act.Name] = async input => {
                        await utils.executeAndCompileAllActions(act.Actions, { Data: input }, this, this.controldatavalue, this.newscopeitems);
                    };
                }

            if (this.scopeitems && this.controlName)
                this.scopeitems[this.controlName] = public_vars;
        },
        compileScopeItemsLegacy(items, public_vars) {
            if (items.Variables) {
                this.uselocalscope = true;

                for (let i = 0; i < items.Variables.length; i++) {
                    const v = items.Variables[i];

                    if (typeof v.InitialValue === 'string' && !v.InitialValue.startsWith('{#')) {
                        const f = utils.compile(this, v.InitialValue);
                        this.newscopeitems[v.Name] = utils.evaluate(f, this);
                    } else {
                        const f = utils.compileObject(this, v.InitialValue);
                        this.newscopeitems[v.Name] = utils.evaluateObject(f, this);
                    }

                    if (v.Public)
                        public_vars[v.Name] = this.newscopeitems[v.Name];
                }

                this.uselocalscope = false;
            }

            const public_funcs = {};
            let js = 'function controller(basicService, scope, api, controlModel, util, promiseHelper)\n{\n';
            if (items.Functions) {
                for (let i = 0; i < items.Functions.length; i++) {
                    const f = items.Functions[i];
                    js += `\nthis.${f.Name} = ${f.Body};\n`;

                    if (f.Public)
                        public_funcs[f.Name] = true;
                }
            }
            js += '}';

            const finalContext = this;
            const api = utils.apiWrapper;
            const chf = careHelpfulFunctions;
            const care = careService;
            const Control = this.newscopeitems;
            const Params = this.controldatavalue;

            let theJavascript = utils.instantiateDynamicControllerScoped(js, { care, api, finalContext, chf, Control, Params }, 'care, null, api, finalContext, chf, Control, Params');

            for (let key in theJavascript)
                if (typeof theJavascript[key] === 'function') {
                    this.newscopeitems[key] = theJavascript[key].bind(this.newscopeitems);

                    if (key in public_funcs)
                        public_vars[key] = this.newscopeitems[key];
                }
        },
        showContextMenu(e) {
            e.preventDefault();
            this.showMenu = false;
            this.x = e.clientX;
            this.y = e.clientY;
            nextTick(() => {
                this.showMenu = true;
            });
        },
        openForLocalEdit(e, fn) {
            e.preventDefault();
            utils.openForLocalEdit({ Type: 'UserControl', Name: fn });
        },
        openForExternalEdit(e, fn, controlOwnerId, shared) {
            e.preventDefault();
            utils.OpenEditorForExternalEdit({ Type: 'UserControl', Name: fn, OwnerId: controlOwnerId }, shared);
        },
        openDocumentStandAlone(e, fn) {
            e.preventDefault();
            utils.OpenDocumentStandAlone({ Name: fn });
        },
        async performEmitControlEvent(action) {
            utils.log(`UserControl EmitControlEvent ${action.ActionData.EventName}`);

            if (action.ActionData.Debug && action.ActionData.Debug.BreakPoint) debugger;

            try {
                const eventName = utils.evaluate(action.eventName, action.context);
                const input = utils.evaluateObject(action.input, action.context);

                if (this.controlDataValue[eventName]) {
                    await utils.executeAndCompileAllActions(this.controlDataValue[eventName], input, this);
                }

                try {
                    await utils.success(action);
                } catch (e) { }
            } catch (e) {
                utils.error(`ActionService EmitControlEvent ${action.ActionData.EventName} failed`, e);

                try {
                    await utils.failure(action);
                } catch (e) { }
            } finally {
                try {
                    await utils.complete(action);
                } catch (e) { }

                action.FinishFunc(true);
            }
        },
        async postRenderComplete() {
            const onMounted = this.newscopeitems?.onMounted;
            if (typeof onMounted === 'function') {
                try {
                    await onMounted();
                } catch (e) {
                    utils.warn(`UserControl ${this.controlName || this.controlURLvalue || ''} onMounted failed`, e);
                }
            }
        },
    },
    render() {
        if (!this.control || !this.control.ControlData) {
            return <div innerHTML="<!-- No control -->"></div>;
        }

        let DynamicControl = utils.getDynamicComponent(h, this.control);

        if (!DynamicControl)
            DynamicControl = 'default-unknown';

        let debug;
        if (this.isdebug)
            debug = {
                controlURLvalue: this.controlURLvalue,
                controlCustomerId: this.headers?.customerid,
                refreshchildren: this.refreshchildren,
                openForLocalEdit: this.openForLocalEdit,
                openForExternalEdit: this.openForExternalEdit,
                openDocumentStandAlone: this.openDocumentStandAlone,
            };

        return (
                <DynamicControl
                    class={{ 'c-UserControl': true, [`c-name-${this.name || 'unnamed'}`]: true }}
                    key={this.key}
                    onFinishedRender={(reference) => this.finishRenderHandler(reference)}
                    type={this.control.ControlType}
                    name={this.control.ControlData.Name}
                    root={this.root}
                    parentType={this.parentType}
                    controlData={this.control.ControlData}
                    paramData={this.controlDataValue}
                    accessPermission={this.accessPermission}
                    payload={this.payload}
                    {...this.$attrs}
                    debug={debug}
                    scopeitems={this.newscopeitems}
                    controlscope={this.controldatavalue}
                    cacheControl={this.control.CacheControl}
                    controlEvents={this.control.Events}
                />
        );
    },
});